import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, AlertTriangle, BookOpen, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { API_BASE } from '../config/api';

const API = API_BASE;

export default function LTILaunch() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [course, setCourse] = useState(null);

  const sessionId = searchParams.get('session');
  const courseId = searchParams.get('course');

  useEffect(() => {
    if (sessionId) {
      initializeLTISession();
    } else {
      setError('Invalid LTI session');
      setLoading(false);
    }
  }, [sessionId, courseId]);

  const initializeLTISession = async () => {
    try {
      // Verify the LTI session
      const sessionRes = await axios.get(`${API}/lti/session/${sessionId}`);
      setSessionData(sessionRes.data);

      // Check if user exists in Cerebro
      const email = sessionRes.data.email;
      
      if (email) {
        // Try to auto-login or show login prompt
        const existingToken = localStorage.getItem('token');
        
        if (existingToken) {
          // User already logged in, redirect to course
          if (courseId) {
            // Load course info
            try {
              const courseRes = await axios.get(`${API}/courses/${courseId}`, {
                headers: { Authorization: `Bearer ${existingToken}` }
              });
              setCourse(courseRes.data);
            } catch (e) {
              console.error('Failed to load course:', e);
            }
          }
          setLoading(false);
        } else {
          // No token, show prompt to login
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    } catch (err) {
      console.error('LTI session error:', err);
      setError('Failed to validate LTI session. Please try launching again from Moodle.');
      setLoading(false);
    }
  };

  const handleContinueToCourse = () => {
    if (courseId) {
      navigate(`/courses/${courseId}?lti_session=${sessionId}`);
    } else {
      navigate(`/courses?lti_session=${sessionId}`);
    }
  };

  const handleLogin = () => {
    // Store LTI session info for after login
    localStorage.setItem('lti_pending_session', sessionId);
    if (courseId) {
      localStorage.setItem('lti_pending_course', courseId);
    }
    navigate('/login?redirect=lti');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-navigate-bg flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-navigate-gold mx-auto mb-4" />
          <p className="text-zinc-400">Connecting to Cerebro...</p>
          <p className="text-zinc-500 text-sm mt-2">Validating your Moodle session</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-navigate-bg flex items-center justify-center p-4">
        <div className="bg-zinc-900/50 rounded-xl border border-zinc-800 p-8 max-w-md text-center">
          <AlertTriangle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Launch Error</h2>
          <p className="text-zinc-400 mb-6">{error}</p>
          <Button
            onClick={() => window.close()}
            variant="outline"
            className="border-zinc-700"
          >
            Close Window
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-navigate-bg flex items-center justify-center p-4">
      <div className="bg-zinc-900/50 rounded-xl border border-zinc-800 p-8 max-w-lg w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-navigate-gold/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <ExternalLink className="h-8 w-8 text-navigate-gold" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Welcome to Cerebro</h1>
          <p className="text-zinc-400">Launched from Moodle LMS</p>
        </div>

        {/* Session Info */}
        {sessionData && (
          <div className="bg-zinc-800/50 rounded-lg p-4 mb-6">
            <p className="text-sm text-zinc-400 mb-1">Learner</p>
            <p className="text-white font-medium">{sessionData.name || sessionData.email || 'Unknown'}</p>
            {sessionData.email && (
              <p className="text-zinc-500 text-sm">{sessionData.email}</p>
            )}
          </div>
        )}

        {/* Course Info */}
        {course && (
          <div className="bg-navigate-gold/10 border border-navigate-gold/30 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <BookOpen className="h-5 w-5 text-navigate-gold mt-0.5" />
              <div>
                <p className="text-sm text-navigate-gold mb-1">Course</p>
                <p className="text-white font-medium">{course.title}</p>
                {course.category && (
                  <p className="text-zinc-400 text-sm">{course.category}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="space-y-3">
          {localStorage.getItem('token') ? (
            <Button
              onClick={handleContinueToCourse}
              className="w-full bg-navigate-gold text-black hover:bg-navigate-gold-light"
              data-testid="lti-continue-btn"
            >
              {courseId ? 'Start Course' : 'Browse Courses'}
            </Button>
          ) : (
            <>
              <Button
                onClick={handleLogin}
                className="w-full bg-navigate-gold text-black hover:bg-navigate-gold-light"
                data-testid="lti-login-btn"
              >
                Sign In to Continue
              </Button>
              <p className="text-center text-zinc-500 text-sm">
                Sign in with your Cerebro account to track your progress
              </p>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-zinc-800 text-center">
          <p className="text-zinc-500 text-xs">
            Session ID: {sessionId?.slice(0, 12)}...
          </p>
        </div>
      </div>
    </div>
  );
}
